import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  SiReact,
  SiJavascript,
  SiTypescript,
  SiNodedotjs,
  SiPython,
  SiMongodb,
  SiGit,
  SiTailwindcss,
  SiExpress,
  SiCplusplus,
  SiPostgresql,
  SiGithub,
  SiPostman,
  SiFastapi,
  SiVercel,
} from "react-icons/si";
import { Plug } from "lucide-react";
// Icon mapping for all skills
const skillIconMap = {
  JavaScript: <SiJavascript className="text-5xl text-yellow-500" />,
  TypeScript: <SiTypescript className="text-5xl text-blue-600" />,
  Python: <SiPython className="text-5xl text-blue-600" />,
  "C++": <SiCplusplus className="text-5xl text-blue-600" />,
  "React.js": <SiReact className="text-5xl text-blue-500" />,
  "React Native": <SiReact className="text-5xl text-blue-700" />,
  "Tailwind CSS": <SiTailwindcss className="text-5xl text-cyan-500" />,
  "Node.js": <SiNodedotjs className="text-5xl text-green-600" />,
  "Express.js": (
    <SiExpress className="text-5xl text-gray-700 dark:text-gray-300" />
  ),
  FastAPI: <SiFastapi className="text-5xl text-teal-500" />,
  "REST APIs": <Plug className="w-11 h-11 text-indigo-500" />,
  "Google Gemini API": (
    <img
      src={process.env.PUBLIC_URL + "/images/skill/Gemini.png"}
      alt="Gemini"
      className="w-11 h-11 rounded-md"
    />
  ),
  LangChain: (
    <img
      src="https://avatars.githubusercontent.com/u/126733545"
      alt="LangChain"
      className="w-11 h-11 rounded-md"
    />
  ),
  FAISS: <span className="text-3xl font-bold text-blue-500">F</span>,
  MongoDB: <SiMongodb className="text-5xl text-green-500" />,
  PostgreSQL: <SiPostgresql className="text-5xl text-blue-800" />,
  Git: <SiGit className="text-5xl text-orange-600" />,
  GitHub: <SiGithub className="text-5xl text-gray-800 dark:text-white" />,
  Postman: <SiPostman className="text-5xl text-orange-500" />,
  Vercel: <SiVercel className="text-5xl text-gray-800 dark:text-white" />,
};

// Unified particle tone for the current visual theme
const particleColorClass = "bg-[#C5C0B0]";

const skillsContainerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.20,
    },
  },
};

const skillItemVariants = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: "easeOut",
    },
  },
};

const Skills = ({ skills }) => {
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = ["All", ...new Set(skills.map((skill) => skill.category))];
  const filteredSkills =
    activeCategory === "All"
      ? skills
      : skills.filter((skill) => skill.category === activeCategory);

  return (
    <section id="skills" className="section-padding bg-[#F5F2E8] dark:bg-gray-900">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Skills & Technologies
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-8"></div>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full transition-all ${
                  activeCategory === category
                    ? "bg-blue-600 text-white"
                    : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-gray-600"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          key={activeCategory}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6"
          variants={skillsContainerVariants}
          initial="hidden"
          animate="show"
        >
          {filteredSkills.map((skill) => (
            <motion.div
              key={skill.name}
              variants={skillItemVariants}
              whileHover={{
                scale: 1.05,
                filter: "drop-shadow(0 14px 28px rgba(0, 0, 0, 0.12))",
              }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative group cursor-pointer perspective-1000"
            >
              {/* Flip wrapper */}
              <div className="relative w-full h-44 ">
                {/* Front Side */}
               <div
  className="absolute inset-0 w-full h-full rounded-2xl 
  bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 
  dark:to-gray-900 p-4 shadow-lg border border-gray-100 
  dark:border-gray-700 flex flex-col items-center 
  justify-center relative"
  style={{
    opacity: 1,
    transition: 'opacity 400ms ease',
  }}
  onMouseEnter={e => e.currentTarget.style.opacity = 0}
  onMouseLeave={e => e.currentTarget.style.opacity = 1}
>
                  {/* Floating particles */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                    {[...Array(3)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{
                          x: [0, 30, 0],
                          y: [0, -20, 0],
                          opacity: [0, 1, 0],
                        }}
                        transition={{
                          duration: 2 + i,
                          repeat: Infinity,
                          delay: i * 0.5,
                        }}
                        className={`absolute w-1 h-1 rounded-full ${particleColorClass}`}
                        style={{
                          left: `${20 + i * 30}%`,
                          top: `${30 + i * 20}%`,
                        }}
                      />
                    ))}
                  </div>

                  <div className="w-14 h-14 mb-3 flex items-center justify-center">
                    {skillIconMap[skill.name] || (
                      <span className="text-3xl font-bold text-gray-400">
                        {skill.name.charAt(0)}
                      </span>
                    )}
                  </div>

                  <h3 className="text-center font-semibold text-gray-900 dark:text-white text-sm">
                    {skill.name}
                  </h3>
                </div>

                {/* Back Side */}
               <div
  className="absolute inset-0 w-full h-full rounded-2xl 
  bg-[#FDDCB5] dark:bg-[#2A2A2A] flex flex-col items-center justify-center 
  shadow-lg text-[#2D2A26] dark:text-white p-3"
  style={{
    opacity: 0,
    transition: 'opacity 400ms ease',
  }}
  onMouseEnter={e => e.currentTarget.style.opacity = 1}
  onMouseLeave={e => e.currentTarget.style.opacity = 0}
>
                  <div className="text-center">
                    <div className="text-sm font-medium mb-1 opacity-80">
                      {skill.category}
                    </div>
                    <div className="font-bold text-la">{skill.name}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
